/**
 * RATTACHEMENT D'UN ÉVÉNEMENT D'ASSOCIATION À LA SOIRÉE DU CLUB — ce que
 * les écrans en disent, sans React (19/10/2026).
 *
 * Le serveur décide (`ticketing/rattachement.ts`) ; ce fichier écrit les
 * phrases : la sous-ligne des listes (« + MEDECINE (Pretix) · rattachée
 * automatiquement »), le nombre affiché, et la conséquence d'un geste AVANT
 * le geste (« ses 209 entrées compteront avec … »).
 */

export type Methode = 'AUTO' | 'MANUEL' | 'MIXTE'

export interface ResumeRattachees {
  libelle: string
  billetterie: string
  source?: string
  nombre: number
  methode: Methode
  ids?: string[]
  decision_ids?: string[]
  /** Les associations de ces événements (20/10/2026) — seulement pour qui les consulte, et si le réglage le permet. */
  associations?: AssociationCourte[]
}

/** Une association, telle que les autres écrans la nomment (« · Médecine »). */
export interface AssociationCourte { id: string; name: string }

export interface RattacheeDansListe {
  id: string
  name: string
  source: string
  billetterie: string
  scanned: number | null
  valid: number | null
  methode: 'AUTO' | 'MANUEL'
  decision_id: string
}

/** Ce que les listes (soirées, calendrier) reçoivent en plus de la soirée. */
export interface ChampsRattachement {
  rattachees?: RattacheeDansListe[]
  rattachees_resume?: ResumeRattachees[]
  rattachee_a?: { id: string; name: string; decision_id: string; methode: 'AUTO' | 'MANUEL' } | null
  total_scanned?: number | null
  total_valid?: number | null
  /** L'association de la soirée elle-même (« BDE PHARMA » sur Shotgun), ou commune à une ligne regroupée (20/10/2026). */
  association?: AssociationCourte | null
}

export interface Raison { ok: boolean; texte: string; code?: string }

export interface SoireeChoix {
  id: string
  name: string
  starts_at: string
  ends_at: string | null
  nuit: string
  meme_nuit: boolean
  scanned: number | null
  valid: number | null
}

export interface ItemARattacher {
  cle: string
  source: string
  billetterie: string
  nuit: string
  libelle: string
  enfants: Array<{ id: string; name: string; starts_at: string; ends_at: string | null; scanned: number | null; valid: number | null }>
  scanned: number | null
  valid: number | null
  verdict: 'CERTAIN' | 'PROBABLE' | 'DEMANDER' | 'AUCUNE'
  suggestion: SoireeChoix | null
  raisons: Raison[]
  choix: SoireeChoix[]
  separee: { decision_ids: string[]; par: string | null; le: string; raison: string | null } | null
  /** Le worker le rattachera seul à sa prochaine passe : personne n'a à décider. */
  auto_bientot?: boolean
}

export interface ListeRattachements {
  a_rattacher: ItemARattacher[]
  /** Rattachés seuls à la prochaine passe (5 min au plus). */
  bientot?: ItemARattacher[]
  seules?: ItemARattacher[]
  separees: ItemARattacher[]
  rattachees: Array<{
    parent: { id: string; name: string; starts_at: string; nuit: string }
    resume: ResumeRattachees
    decided_at: string
    decided_by: string | null
    confiance: 'CERTAIN' | 'PROBABLE' | 'MANUEL' | 'MIXTE'
  }>
  attach_mode: 'CERTAIN_AND_PROBABLE' | 'CERTAIN_ONLY' | 'NEVER'
  motif_min: number
  /** L'alerte « avant la nuit » de chaque cas à venir (20/10/2026) ; absent d'un serveur plus ancien. */
  alertes?: EtatAlerteCas[]
}

/** « Prévenu le 14/10 à 16 h 05 », « Alerte prévue le 14/10 à 14 h » (le serveur écrit la phrase). */
export interface EtatAlerteCas {
  cle: string
  genre: 'A_RATTACHER' | 'SEULE'
  nuit: string
  event_ids: string[]
  etat: 'ENVOYEE' | 'PREVUE' | 'SANS_DESTINATAIRE' | 'ETEINTE' | 'SANS_VENTE' | 'PASSEE'
  a: string | null
  attente_club: boolean
  phrase: string
}

/**
 * L'ancre d'un cas dans « À décider » — PURE. Le lien de l'alerte
 * (`?vue=a-decider&cas=pretix|2026-10-15`) défile jusqu'à elle.
 */
export function idDuCas(cle: string): string {
  return `ratt-${cle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
}

/** Le cas visé par un lien (`cas=pretix|2026-10-15`) — PURE ; `null` s'il est absent ou illisible. */
export function casDuLien(search: string): string | null {
  const c = new URLSearchParams(search).get('cas')
  return c && /^[a-z]+\|\d{4}-\d{2}-\d{2}$/.test(c) ? c : null
}

/**
 * Où mène le lien de l'alerte pour qui gère les soirées SANS consulter les
 * associations — PURE (revue du 20/10/2026). L'alerte va à « Gérer les
 * soirées » ; son lien ouvre Associations › À décider. Sans « Consulter les
 * associations », la même liste vit dans Réglages Soirées › Billetteries :
 * on y va, ouvert sur le cas. `null` : rien à rediriger.
 */
export function redirectionADecider(permissions: readonly string[], search: string): string | null {
  if (permissions.includes('association.read') || !permissions.includes('soiree.read')) return null
  if (new URLSearchParams(search).get('vue') !== 'a-decider') return null
  const cas = casDuLien(search)
  return `/settings/soirees/billetteries${cas ? `?cas=${encodeURIComponent(cas)}` : ''}`
}

/** L'état d'alerte d'un cas (et de son genre) — PURE. */
export function alerteDuCas(alertes: readonly EtatAlerteCas[] | undefined, cle: string, genre: EtatAlerteCas['genre']): EtatAlerteCas | null {
  return alertes?.find((a) => a.cle === cle && a.genre === genre) ?? null
}

/**
 * Les cas « pas encore de soirée du club » À VENIR — PURE : les « seules »
 * dont la nuit n'est pas passée (le serveur ne rend leur alerte que pour
 * celles-là). Ni « Garder séparée » ni urgence : Gestion les rattachera seul
 * si le club ouvre sa soirée.
 */
export function seulesAVenir(liste: Pick<ListeRattachements, 'seules' | 'alertes'>): ItemARattacher[] {
  const cles = new Set((liste.alertes ?? []).filter((a) => a.genre === 'SEULE').map((a) => a.cle))
  return (liste.seules ?? []).filter((i) => cles.has(i.cle)).sort((a, b) => a.nuit.localeCompare(b.nuit))
}


export interface RattachementDeLaSoiree {
  role: 'SOIREE' | 'RATTACHEE' | 'A_RATTACHER' | 'SEPAREE' | 'SEULE'
  parent: {
    id: string; name: string; starts_at: string; nuit: string
    decision_id: string; methode: 'AUTO' | 'MANUEL'; confiance: 'CERTAIN' | 'PROBABLE' | 'MANUEL'
    decided_at: string; decided_by: string | null; raison: string | null
  } | null
  enfants: RattacheeDansListe[]
  resume: ResumeRattachees[]
  total: { scanned: number | null; valid: number | null } | null
  item: ItemARattacher | null
  /** « Soirée association » (19/10/2026) : absent d'un serveur plus ancien. */
  soiree_association?: SoireeAssociation | null
}

/** La billetterie du club ne vend presque rien, les associations portent la nuit. */
export interface SoireeAssociation {
  libelle: 'Soirée association'
  club_valid: number
  associations_valid: number
  tuiles_masquees: string[]
  raison: string
}

/* ═══ annoncé avant la nuit (19/10/2026) ═══ */

export interface AnnonceAVenir {
  cle: string
  etat: 'RATTACHEE' | 'BIENTOT'
  nuit: string
  libelle: string
  billetterie: string
  enfants: string[]
  valid: number | null
  parent: { id: string; name: string; starts_at: string }
  methode: Methode | null
  confiance: 'CERTAIN' | 'PROBABLE' | 'MANUEL' | 'MIXTE' | null
  phrase: string
  detail: string
}

export interface RattachementsAVenir {
  actif: boolean
  horizon_jours: number
  annonces: AnnonceAVenir[]
  a_rattacher: number
}

/** Le titre du bloc : « 2 rattachements annoncés » — `null` quand il n'y a rien à dire. PURE. */
export function titreAVenir(r: Pick<RattachementsAVenir, 'actif' | 'annonces' | 'a_rattacher'> | null | undefined): string | null {
  if (!r || !r.actif) return null
  const n = r.annonces.length
  if (n === 0 && r.a_rattacher === 0) return null
  if (n === 0) return `${r.a_rattacher} événement${r.a_rattacher > 1 ? 's' : ''} d’association à rattacher pour les prochaines nuits`
  return n === 1 ? 'Rattachement annoncé pour une prochaine nuit' : `${n} rattachements annoncés pour les prochaines nuits`
}

/** « rattachée automatiquement », « rattachées à la main », « rattachées » (mélange). */
export function texteMethode(m: Methode, nombre: number): string {
  const s = nombre > 1 ? 'rattachées' : 'rattachée'
  if (m === 'AUTO') return `${s} automatiquement`
  if (m === 'MANUEL') return `${s} à la main`
  return s
}

/** La sous-ligne d'une soirée dans une liste : « + MEDECINE (Pretix) · rattachée automatiquement ». */
export function libelleSousLigne(r: Pick<ResumeRattachees, 'libelle' | 'billetterie' | 'nombre' | 'methode'>): string {
  return `+ ${r.libelle} (${r.billetterie}) · ${texteMethode(r.methode, r.nombre)}`
}

/** Le nombre affiché d'une ligne : soirée + rattachés quand le serveur l'a calculé (réglage), sinon le sien. */
export function nombreAffiche(v: { tickets_scanned: number | null; total_scanned?: number | null }): number | null {
  return v.total_scanned ?? v.tickets_scanned
}

/**
 * LES ENTRÉES DE TOUTES LES BILLETTERIES de la soirée : la sienne + celles des
 * rattachés, QUEL QUE SOIT le réglage du nombre affiché (revue du 19/10/2026).
 * Avec « La soirée seule », la fiche rapide du calendrier disait « 710 entrées
 * toutes billetteries, dont 710 pour la soirée elle-même » le 15/10 (934 en
 * vrai) : le réglage choisit le nombre d'une ligne, jamais ce total-là.
 */
export function totalToutesBilletteries(v: { tickets_scanned: number | null; rattachees?: ReadonlyArray<{ scanned: number | null }> | undefined }): number | null {
  const xs = [v.tickets_scanned, ...(v.rattachees ?? []).map((r) => r.scanned)]
  return xs.every((x) => x === null) ? null : xs.reduce<number>((a, x) => a + (x ?? 0), 0)
}

/** Le signal qui fait relire toutes les listes de rattachements ouvertes après un geste (bandeau, Réglages, panneau). */
export const EVT_RATTACHEMENTS = 'gestion:rattachements'

const ddmm = (nuit: string): string => `${nuit.slice(8, 10)}/${nuit.slice(5, 7)}`
const entrees = (n: number): string => `${n.toLocaleString('fr-FR').replace(/[  ]/g, ' ')} entrée${n > 1 ? 's' : ''}`

/**
 * LA CONSÉQUENCE D'UN RATTACHEMENT, dite avant le geste. Même nuit : rien ne
 * bouge en Comptabilité. Autre nuit : l'événement change de nuit, en
 * Comptabilité aussi — c'est pourquoi un motif est alors demandé.
 */
export function phraseConsequence(
  item: { scanned: number | null; libelle: string; nombre: number },
  cible: { name: string; meme_nuit: boolean; nuit: string },
): string {
  const n = item.scanned ?? 0
  const sujet = n > 0
    ? `${item.nombre > 1 ? 'Leurs' : 'Ses'} ${entrees(n)} compteront`
    : `${item.nombre > 1 ? 'Ces événements compteront' : 'Cet événement comptera'}`
  const base = `${sujet} avec « ${cible.name} » dans les listes et le calendrier.`
  return cible.meme_nuit
    ? `${base} La Comptabilité ne change pas.`
    : `${base} ${item.nombre > 1 ? 'Ils passent' : 'Il passe'} dans la nuit du ${ddmm(cible.nuit)}, en Comptabilité aussi : dites pourquoi.`
}

/** « Nuit du 15/10 » pour un élément. */
export function nuitCourte(nuit: string): string {
  return `Nuit du ${ddmm(nuit)}`
}

/**
 * Ce que dit le verdict, en tête d'un élément. « Gestion le rattacherait
 * seul » se lisait « il ne le fera pas » (revue du 19/10/2026) : l'élément dit
 * ce qui va se passer, ou pourquoi on vous demande.
 */
export function texteVerdict(item: Pick<ItemARattacher, 'verdict' | 'auto_bientot'>): string {
  const force = item.verdict === 'CERTAIN' ? 'certain' : 'probable'
  if (item.verdict === 'AUCUNE') return 'Aucune soirée du club cette nuit-là : vous pouvez la rattacher à une soirée de la veille ou du lendemain'
  if (item.auto_bientot) return `Gestion le rattache seul à son prochain passage, dans 5 minutes au plus (${force}) : rien à faire`
  if (item.verdict === 'CERTAIN' || item.verdict === 'PROBABLE') return `Rattachement ${force}, mais le réglage vous laisse décider`
  return 'Gestion vous demande'
}

/* ═══ « Prochaine soirée de Médecine » (Accueil, Centre d'activité — 20/10/2026) ═══ */

export interface ProchaineSoireeAssociation {
  association: AssociationCourte
  nuit: string
  vendus: number
  event_id: string
}

export interface ProchainesAssociations {
  actif: boolean
  horizon_jours: number
  prochaines: ProchaineSoireeAssociation[]
}

const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']

/** « jeu. 22/10 » — PURE (la nuit est une date, jamais un instant : pas de fuseau). */
export function jourCourtDeLaNuit(nuit: string): string {
  const [a, m, j] = nuit.split('-').map(Number)
  const jour = new Date(Date.UTC(a!, m! - 1, j!)).getUTCDay()
  return `${JOURS_COURTS[jour]} ${nuit.slice(8, 10)}/${nuit.slice(5, 7)}`
}

/**
 * « de Médecine », « d’ESN », « d’Intersanté » — PURE (la même règle que le
 * serveur, `deNom`). Sept des quinze associations suggérées commencent par
 * une voyelle : « Prochaine soirée de ESN » n'est pas du français.
 */
export function deNom(nom: string): string {
  return /^[aeiouyàâäéèêëîïôöùûüœæ]/i.test(nom.trim()) ? `d’${nom}` : `de ${nom}`
}

/** « Soirée de » ou « Soirée d’ », devant le nom (un lien) — PURE. */
export function prefixeSoireeDe(nom: string): string {
  return deNom(nom).startsWith('d’') ? 'Soirée d’' : 'Soirée de '
}

/**
 * Ce que Gestion fera quand le club ouvrira sa soirée, pour les cas « pas
 * encore de soirée du club » — PURE, selon le RÉGLAGE (revue du 20/10/2026 :
 * « Gestion les rattachera seul » s'écrivait même avec « jamais »).
 */
export function phraseSeules(mode: ListeRattachements['attach_mode']): string {
  const base = 'Ces soirées ont vendu des billets, mais le club n’a encore aucune soirée cette nuit-là : elles comptent à part.'
  const ensuite = mode === 'NEVER'
    ? ' Le rattachement automatique est éteint : quand le club ouvrira sa soirée, il faudra les rattacher à la main.'
    : mode === 'CERTAIN_ONLY'
      ? ' Quand le club ouvrira sa soirée dans Shotgun, Gestion les rattachera seul seulement si les deux portent le même nom et que la date annoncée est sûre ; sinon, il vous le demandera ici.'
      : ' Quand le club ouvrira sa soirée dans Shotgun, Gestion les rattachera seul si la règle le permet (même nuit, horaires qui se recouvrent, date annoncée sûre) ; sinon, il vous le demandera ici.'
  return `${base}${ensuite} Rattachez-les à la main dès maintenant seulement si elles appartiennent à une soirée de la veille ou du lendemain.`
}

/** La même chose pour UNE soirée (fiche d'une association) — PURE. */
export function phraseSeuleUne(mode: ListeRattachements['attach_mode'] | undefined): string {
  if (mode === 'NEVER') return 'Pas encore de soirée du club cette nuit-là, et le rattachement automatique est éteint : quand le club ouvrira la sienne, il faudra la rattacher à la main.'
  return 'Pas encore de soirée du club cette nuit-là : quand le club ouvrira la sienne, Gestion la rattachera seul si la règle le permet, sinon il vous le demandera.'
}

/** « Prochaine soirée de Médecine : jeu. 22/10 · 249 billets » — PURE. */
export function phraseProchaineAssociation(p: Pick<ProchaineSoireeAssociation, 'association' | 'nuit' | 'vendus'>): string {
  const billets = p.vendus > 0 ? ` · ${p.vendus.toLocaleString('fr-FR').replace(/[  ]/g, ' ')} billet${p.vendus > 1 ? 's' : ''}` : ' · aucun billet vendu encore'
  return `Prochaine soirée ${deNom(p.association.name)} : ${jourCourtDeLaNuit(p.nuit)}${billets}`
}

/**
 * Le titre du panneau « à venir » quand il porte AUSSI les prochaines
 * soirées d'associations — PURE. Un seul panneau, jamais une deuxième liste.
 */
export function titrePanneauAVenir(r: Pick<RattachementsAVenir, 'actif' | 'annonces' | 'a_rattacher'> | null | undefined, prochaines: number): string | null {
  const t = titreAVenir(r)
  if (t) return t
  if (prochaines === 0) return null
  return prochaines === 1 ? 'Prochaine soirée d’association' : 'Prochaines soirées d’associations'
}
